import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import baseURL from '../utils/baseURL';
import useAxiosAuth from '../hooks/useAxiosAuth';

// PLATFORM REPORTS — the Super Admin's own numbers: the landing dashboard,
// the tenant report and the tenant count. Served by /api/platform/reports/*
// (Backend/api/routes/platformReports.js) behind the `reports.view` catalogue
// key; every call already spans the whole platform, so nothing here is
// tenant-scoped. The patient report / count keep using reportsSlice.js — they
// reuse the tenant app's endpoints, running unscoped.

const REPORTS_URL = `${baseURL}/platform/reports`;

// Helper function to extract serializable error information
const getSerializableError = (error) => {
    if (!error) return 'An unknown error occurred';
    if (typeof error === 'string') return error;
    if (error.response) {
        // 400s carry a plain-string message (platformReportsController#bad);
        // guard the `{ en }` envelope other controllers use so a toast never
        // receives an object.
        const msg = error.response.data?.message;
        return (msg && typeof msg === 'object' ? msg.en : msg)
            || error.message
            || `Request failed with status code ${error.response.status}`;
    }
    return error.message || 'An error occurred';
};

const errorMessage = (action, fallback) =>
    (typeof action.payload?.error === 'string' ? action.payload.error : (action.payload?.error?.message || fallback));

// Drop empty filter values so the query string only carries what was chosen.
const cleanParams = (params = {}) =>
    Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v !== null && v !== undefined));

// The viewer's IANA zone ('Asia/Kolkata'). The five report endpoints accept
// an optional `tz` so the from/to boundaries, the "today" KPI and the period
// buckets follow the viewer's calendar instead of UTC. Omitted when the
// browser cannot say — the API then defaults to UTC.
const viewerTimeZone = () => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
    } catch {
        return undefined;
    }
};
const withTz = (params = {}) => cleanParams({ ...params, tz: viewerTimeZone() });

// GET /dashboard?from&to&countryId&tenantType
export const getPlatformDashboard = createAsyncThunk(
    'platformReports/getPlatformDashboard',
    async (params = {}, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${REPORTS_URL}/dashboard`, { params: withTz(params) });
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// GET /filters — the option lists the three pages' filter popovers offer.
export const getPlatformReportFilters = createAsyncThunk(
    'platformReports/getPlatformReportFilters',
    async (_, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${REPORTS_URL}/filters`);
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// GET /tenants?from&to&page&limit&search&status&tenantType&countryId&connectionMode&sortBy&sortDir
export const getTenantsReport = createAsyncThunk(
    'platformReports/getTenantsReport',
    async (params = {}, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${REPORTS_URL}/tenants`, { params: withTz(params) });
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// GET /tenants/download (same filters) → xlsx blob
export const downloadTenantsReportExcel = createAsyncThunk(
    'platformReports/downloadTenantsReportExcel',
    async (params = {}, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${REPORTS_URL}/tenants/download`, {
                params: withTz(params),
                responseType: 'blob',
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// GET /tenant-count?from&to&groupBy=day|month&tenantType&countryId
export const getTenantCount = createAsyncThunk(
    'platformReports/getTenantCount',
    async (params = {}, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${REPORTS_URL}/tenant-count`, { params: withTz(params) });
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// GET /tenant-count/download → xlsx blob
export const downloadTenantCountExcel = createAsyncThunk(
    'platformReports/downloadTenantCountExcel',
    async (params = {}, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${REPORTS_URL}/tenant-count/download`, {
                params: withTz(params),
                responseType: 'blob',
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

const EMPTY_FILTERS = { tenantTypes: [], countries: [], statuses: [], connectionModes: [] };

const platformReportsSlice = createSlice({
    name: 'platformReports',
    initialState: {
        dashboard: null,
        dashboardLoading: false,
        filters: EMPTY_FILTERS,
        filtersLoading: false,
        tenants: [],
        tenantsTotalItems: 0,
        tenantsLoading: false,
        tenantsDownloadLoading: false,
        tenantCount: [],
        tenantCountSummary: { total: 0, byType: {} },
        // `types: [{ value, label }]` the endpoint returns alongside the rows —
        // the series legend / order; falls back to the /filters list.
        tenantCountTypes: [],
        tenantCountLoading: false,
        tenantCountDownloadLoading: false,
        error: null,
    },
    reducers: {
        clearPlatformReportsError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Dashboard
            .addCase(getPlatformDashboard.pending, (state) => {
                state.dashboardLoading = true;
                state.error = null;
            })
            .addCase(getPlatformDashboard.fulfilled, (state, action) => {
                state.dashboardLoading = false;
                state.dashboard = action.payload?.data || null;
            })
            .addCase(getPlatformDashboard.rejected, (state, action) => {
                state.dashboardLoading = false;
                state.error = errorMessage(action, 'Failed to load the platform dashboard');
            })
            // Filters
            .addCase(getPlatformReportFilters.pending, (state) => {
                state.filtersLoading = true;
            })
            .addCase(getPlatformReportFilters.fulfilled, (state, action) => {
                state.filtersLoading = false;
                state.filters = { ...EMPTY_FILTERS, ...(action.payload?.data || {}) };
            })
            .addCase(getPlatformReportFilters.rejected, (state, action) => {
                state.filtersLoading = false;
                state.error = errorMessage(action, 'Failed to load report filters');
            })
            // Tenant report
            .addCase(getTenantsReport.pending, (state) => {
                state.tenantsLoading = true;
                state.error = null;
            })
            .addCase(getTenantsReport.fulfilled, (state, action) => {
                state.tenantsLoading = false;
                state.tenants = action.payload?.data || [];
                state.tenantsTotalItems = action.payload?.totalItems || 0;
            })
            .addCase(getTenantsReport.rejected, (state, action) => {
                state.tenantsLoading = false;
                state.error = errorMessage(action, 'Failed to load the tenant report');
            })
            .addCase(downloadTenantsReportExcel.pending, (state) => {
                state.tenantsDownloadLoading = true;
                state.error = null;
            })
            .addCase(downloadTenantsReportExcel.fulfilled, (state) => {
                state.tenantsDownloadLoading = false;
            })
            .addCase(downloadTenantsReportExcel.rejected, (state, action) => {
                state.tenantsDownloadLoading = false;
                state.error = errorMessage(action, 'Failed to download the tenant report');
            })
            // Tenant count
            .addCase(getTenantCount.pending, (state) => {
                state.tenantCountLoading = true;
                state.error = null;
            })
            .addCase(getTenantCount.fulfilled, (state, action) => {
                state.tenantCountLoading = false;
                state.tenantCount = action.payload?.data || [];
                state.tenantCountSummary = action.payload?.summary || { total: 0, byType: {} };
                state.tenantCountTypes = action.payload?.types || [];
            })
            .addCase(getTenantCount.rejected, (state, action) => {
                state.tenantCountLoading = false;
                state.error = errorMessage(action, 'Failed to load the tenant count');
            })
            .addCase(downloadTenantCountExcel.pending, (state) => {
                state.tenantCountDownloadLoading = true;
                state.error = null;
            })
            .addCase(downloadTenantCountExcel.fulfilled, (state) => {
                state.tenantCountDownloadLoading = false;
            })
            .addCase(downloadTenantCountExcel.rejected, (state, action) => {
                state.tenantCountDownloadLoading = false;
                state.error = errorMessage(action, 'Failed to download the tenant count');
            });
    },
});

export const { clearPlatformReportsError } = platformReportsSlice.actions;
export default platformReportsSlice.reducer;
